import { z } from 'zod';
import {
  AgentSessionView,
  EnsureWorkItem,
  EnsureWorkResult,
  EvidenceView,
  FinishAgentSession,
  ProgressEntryView,
  RecordProgress,
  SetBlocker,
  StartAgentSession,
  TransitionWork,
  WorkCandidate,
  WorkItemListItem,
  WorkItemView,
  AttachEvidence,
} from './work.js';
import { ConfirmRequest, PrepareRequest, PrepareRequestResult, RequestListItem, RequestView, SubmitRequest, TransitionRequest } from './requests.js';
import { AddComment, CommentView } from './collaboration.js';
import { QueryReport, ReportResult } from './reports.js';
import { Priority } from './enums.js';
import { Uuid, Version } from './common.js';

/** Client-generated idempotency key carried by every mutating tool. */
const RequestId = z.string().min(8).max(100).describe('Client-generated idempotency key, stable across retries of the same call');

export const McpScope = z.enum(['work:read', 'work:write', 'requests:read', 'requests:write', 'reports:read']);
export type McpScope = z.infer<typeof McpScope>;

export interface McpToolDefinition {
  name: string;
  title: string;
  description: string;
  input: z.ZodObject;
  output: z.ZodType;
  scope: McpScope;
  annotations: { readOnlyHint: boolean; destructiveHint: boolean; idempotentHint: boolean; openWorldHint: boolean };
}

const read = { readOnlyHint: true, destructiveHint: false, idempotentHint: true, openWorldHint: false };
const write = { readOnlyHint: false, destructiveHint: false, idempotentHint: true, openWorldHint: false };

export const ContextView = z.object({
  user: z.object({ id: Uuid, displayName: z.string(), locale: z.string(), timezone: z.string() }),
  capabilities: z.array(z.string()),
  scopes: z.array(McpScope),
  projects: z.array(z.object({ id: Uuid, key: z.string(), name: z.string() })),
  teams: z.array(z.object({ id: Uuid, name: z.string(), role: z.string() })),
  activeSessions: z.array(z.object({ id: Uuid, client: z.string(), clientSessionRef: z.string(), workItemKeys: z.array(z.string()) })),
  guidance: z.string(),
});

export const mcpTools = {
  get_context: {
    name: 'get_context',
    title: 'Get work-tracking context',
    description:
      'Returns the authenticated person, capabilities, granted scopes, accessible projects/teams and active agent sessions. Call first in a session. Never returns an organization-wide user list.',
    input: z.object({ clientSessionRef: z.string().max(200).optional() }),
    output: ContextView,
    scope: 'work:read',
    annotations: read,
  },
  search_work: {
    name: 'search_work',
    title: 'Search work items',
    description:
      'Permission-filtered candidate search with explainable association signals. Results are suggestions only: similarity never auto-links a session to a task. Max 20 hits.',
    input: z.object({
      query: z.string().min(1).max(500),
      projectId: Uuid.optional(),
      repositoryFullName: z.string().max(200).optional(),
      includeDone: z.boolean().optional(),
    }),
    output: z.object({ hits: z.array(WorkCandidate.extend({ phase: z.string(), updatedAt: z.string() })) }),
    scope: 'work:read',
    annotations: read,
  },
  get_work_item: {
    name: 'get_work_item',
    title: 'Get a work item',
    description: 'Current work item state, permitted recent progress (max 20 entries) and evidence. Accepts a UUID or display key such as VAM-42.',
    input: z.object({ idOrKey: z.string().min(1).max(60) }),
    output: z.object({ workItem: WorkItemView, recentProgress: z.array(ProgressEntryView), evidence: z.array(EvidenceView) }),
    scope: 'work:read',
    annotations: read,
  },
  search_requests: {
    name: 'search_requests',
    title: 'Search requests',
    description: 'Authorized requests and their functional state. Max 20 hits.',
    input: z.object({ query: z.string().max(500).optional(), status: z.string().max(100).optional(), mine: z.boolean().optional() }),
    output: z.object({ hits: z.array(RequestListItem) }),
    scope: 'requests:read',
    annotations: read,
  },
  get_request: {
    name: 'get_request',
    title: 'Get a request',
    description: 'Request detail with functional progress, recent comments and requester feedback. Accepts a UUID or key such as REQ-12.',
    input: z.object({ idOrKey: z.string().min(1).max(60) }),
    output: z.object({ request: RequestView, recentComments: z.array(CommentView) }),
    scope: 'requests:read',
    annotations: read,
  },
  ensure_work_item: {
    name: 'ensure_work_item',
    title: 'Ensure a work item',
    description:
      'Resolve an explicit key or stored session/branch/PR binding, or create one coherent unit of work idempotently by workIntentId. Ambiguity returns needs_selection with candidates and makes no hidden choice. Persist workIntentId locally before the first call.',
    input: EnsureWorkItem.extend({ requestId: RequestId }),
    output: EnsureWorkResult,
    scope: 'work:write',
    annotations: write,
  },
  start_work_session: {
    name: 'start_work_session',
    title: 'Start or resume a work session',
    description: 'Start or resume an agent session for the authenticated person. Unique per person, client and clientSessionRef.',
    input: StartAgentSession.extend({ requestId: RequestId }),
    output: AgentSessionView,
    scope: 'work:write',
    annotations: write,
  },
  record_progress: {
    name: 'record_progress',
    title: 'Record progress',
    description:
      'Append a bounded milestone summary (what changed, why, how, checks actually executed with claimBasis, results, pending work). Append-only; never overwrites human edits. Agent-reported checks stay distinct from CI-observed results.',
    input: RecordProgress.extend({ sessionId: Uuid, requestId: RequestId }),
    output: ProgressEntryView,
    scope: 'work:write',
    annotations: write,
  },
  set_work_phase: {
    name: 'set_work_phase',
    title: 'Request a work phase transition',
    description:
      'Validated transition under the completion policy. Cannot assert unproven production delivery: PRODUCTION work reaches DONE only from verified deployment evidence.',
    input: TransitionWork.extend({ workItemId: Uuid, requestId: RequestId }),
    output: WorkItemView,
    scope: 'work:write',
    annotations: write,
  },
  set_blocker: {
    name: 'set_blocker',
    title: 'Open or resolve a blocker',
    description: 'Open a blocker with a reason (optionally preventing completion) or resolve one.',
    input: z.object({ workItemId: Uuid, requestId: RequestId, blocker: SetBlocker }),
    output: WorkItemView,
    scope: 'work:write',
    annotations: write,
  },
  attach_evidence: {
    name: 'attach_evidence',
    title: 'Attach evidence',
    description: 'Add a source reference. The server assigns AGENT_REPORTED basis; only trusted adapters can produce PROVIDER_VERIFIED evidence.',
    input: AttachEvidence.extend({ requestId: RequestId }),
    output: EvidenceView,
    scope: 'work:write',
    annotations: write,
  },
  finish_work_session: {
    name: 'finish_work_session',
    title: 'Finish a work session',
    description: 'End the session and record a handoff. Ending a session never marks work done.',
    input: FinishAgentSession.extend({ sessionId: Uuid, requestId: RequestId }),
    output: AgentSessionView,
    scope: 'work:write',
    annotations: write,
  },
  prepare_request: {
    name: 'prepare_request',
    title: 'Prepare a request draft',
    description:
      'Create or update a structured request draft as the authenticated requester. Returns missing fields and authorized duplicate candidates. Unknown impact is valid. Does not submit.',
    input: PrepareRequest.extend({ requestId: RequestId }),
    output: PrepareRequestResult,
    scope: 'requests:write',
    annotations: write,
  },
  submit_request: {
    name: 'submit_request',
    title: 'Submit a reviewed request',
    description: 'Submit the draft version the person reviewed. Returns the canonical request URL.',
    input: SubmitRequest.extend({ requestId: RequestId }),
    output: RequestView,
    scope: 'requests:write',
    annotations: write,
  },
  confirm_request: {
    name: 'confirm_request',
    title: 'Confirm or report a resolved request',
    description: 'Record the authenticated requester’s CONFIRMED or PERSISTS response for the displayed fulfillment generation.',
    input: ConfirmRequest.extend({ id: Uuid, requestId: RequestId }),
    output: RequestView,
    scope: 'requests:write',
    annotations: write,
  },
  triage_request: {
    name: 'triage_request',
    title: 'Triage a request',
    description: 'Apply an allowed transition, assignment or priority change, each checked against the person’s capabilities.',
    input: z.object({
      id: Uuid,
      requestId: RequestId,
      expectedVersion: Version,
      transition: TransitionRequest.omit({ expectedVersion: true }).optional(),
      assigneeId: Uuid.nullable().optional(),
      priority: Priority.optional(),
    }),
    output: RequestView,
    scope: 'requests:write',
    annotations: write,
  },
  add_comment: {
    name: 'add_comment',
    title: 'Comment',
    description: 'Comment on an accessible request or work item.',
    input: AddComment.extend({ requestId: RequestId }),
    output: CommentView,
    scope: 'work:write',
    annotations: write,
  },
  query_report: {
    name: 'query_report',
    title: 'Query a report',
    description: 'Execute an allowlisted report. Tier and row-level permissions are identical to the web UI and CSV export.',
    input: QueryReport,
    output: ReportResult,
    scope: 'reports:read',
    annotations: read,
  },
} satisfies Record<string, McpToolDefinition>;

export type McpToolName = keyof typeof mcpTools;
export { WorkItemListItem };
